/**
 * A unified table pagination component that combines pagination UI with pagination logic
 */
import React from 'react';
import { PageSizeSelector, ItemsCountDisplay, PaginationNavigation } from 'src/presentations/components/atom/pagination/PaginationElements';

export interface UnifiedTablePaginationProps {
  // State and total values
  pageIndex: number;
  pageSize: number;
  totalItems: number;

  // Event handlers
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;

  // Options
  pageSizeOptions?: number[];
  hideItemsCount?: boolean;
}

/**
 * A fully self-contained pagination component with UI and logic combined
 * This replaces the combination of useTablePagination hook + PaginatioWithPageSize component
 */
export const UnifiedTablePagination: React.FC<UnifiedTablePaginationProps> = ({
  pageIndex,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  hideItemsCount = false,
}) => {
  // Calculate the total number of pages
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Convert 0-based pageIndex to 1-based currentPage for display
  const currentPage = pageIndex + 1;

  return (
    <div className='table__pagination w-full'>
      <div className='flex items-center gap-8'>
        {!hideItemsCount && (
          <div className='flex items-center'>
            <ItemsCountDisplay pageSize={pageSize} total={totalItems}>
              <PageSizeSelector pageSize={pageSize} pageSizeOptions={pageSizeOptions} onChangePageSize={onPageSizeChange} />
            </ItemsCountDisplay>
          </div>
        )}
        <div className={hideItemsCount ? '' : 'ml-auto'}>
          <PaginationNavigation currentPage={currentPage} totalPages={totalPages} onChangePage={onPageChange} />
        </div>
      </div>
    </div>
  );
};

export default UnifiedTablePagination;
